import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { format } from 'date-fns/format';
import pick from 'lodash-es/pick';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDeleteProfile, useUpdateProfile } from '../../hooks/useProfile';

import { usePopupsContext } from '../../contexts/PopupsContext';
import BasicButton from '../buttons/BasicButton';
import FormButton from '../buttons/FormButton';
import DateField from '../fields/DateField';
import TextField from '../fields/TextField';
import FormStatus from '../partials/FormStatus';
import DeleteAccountConfirmModal from '../popups/DeleteAccountConfirmModal';
import FormControl from './FormControl';
import FormRow from './FormRow';

import styles from '../../styles/forms/profile-form.module.css';

export interface ProfileFormProps {
    action: string;
    withDelete?: boolean;
    className?: string | null;
    onComplete?: ((newUser: User) => void) | null;
}

function ProfileForm({
    action,
    withDelete = false,
    className = null,
    onComplete = null,
}: ProfileFormProps) {
    const user = useUser();
    const tracking = useTracking();
    const { updateProfileAsync } = useUpdateProfile();
    const { deleteProfileAsync } = useDeleteProfile();
    const formFields = ['firstname', 'lastname', 'postalcode', 'birthdate'];

    const now = format(new Date(), 'yyyy-MM-dd');

    const initialValue = pick(user, formFields);

    const onFormComplete = (newUser) => {
        if (onComplete !== null) {
            onComplete(newUser);
        }
    };

    const { fields, onSubmit, status, errors } = useForm({
        fields: formFields,
        initialValue,
        postForm: (formAction, data) => updateProfileAsync(data),
        onComplete: onFormComplete,
    });

    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const onClickDelete = () => setDeleteConfirm(true);
    const onDeleteConfirmClosed = () => setDeleteConfirm(false);
    const { addMessage } = usePopupsContext();
    const onDeleteConfirmed = () => {
        setDeleteConfirm(false);
        deleteProfileAsync().then(() => {
            addMessage('profile_deleted');
            tracking.trackEvent('profile', 'delete');
        });
    };

    return (
        <form
            action={action}
            method="post"
            onSubmit={onSubmit}
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                },
                className,
            ])}
        >
            <FormRow className={styles.formRow}>
                <FormControl
                    label={<FormattedMessage defaultMessage="Prénom" description="Field label" />}
                    className={classNames([styles.formControl, styles.firstName])}
                    {...fields.firstname}
                    withoutErrors
                >
                    <TextField withBorder {...fields.firstname} className={styles.field} />
                </FormControl>
                <FormControl
                    label={<FormattedMessage defaultMessage="Nom" description="Field label" />}
                    className={styles.formControl}
                    {...fields.lastname}
                    withoutErrors
                >
                    <TextField withBorder {...fields.lastname} className={styles.field} />
                </FormControl>
            </FormRow>
            <FormRow alwaysHorizontal className={styles.formRow}>
                <FormControl
                    label={
                        <FormattedMessage defaultMessage="Code postal" description="Field label" />
                    }
                    className={styles.formControl}
                    {...fields.postalcode}
                    withoutErrors
                >
                    <TextField withBorder {...fields.postalcode} className={styles.field} />
                </FormControl>
                <FormControl
                    label={
                        <FormattedMessage
                            defaultMessage="Date de naissance"
                            description="Field label"
                        />
                    }
                    className={styles.formControl}
                    {...fields.birthdate}
                    withoutErrors
                >
                    <DateField
                        withBorder
                        {...fields.birthdate}
                        min="1900-01-01"
                        max={now}
                        className={styles.field}
                    />
                </FormControl>
            </FormRow>
            <div className={styles.actions}>
                {withDelete ? (
                    <BasicButton onClick={onClickDelete} className={styles.deleteButton}>
                        <FormattedMessage
                            defaultMessage="Supprimer le compte"
                            description="Button label"
                        />
                    </BasicButton>
                ) : null}
                <FormStatus status={status} className={styles.status} />
                <FormButton type="submit" className={styles.button} disabled={status === 'loading'}>
                    <FormattedMessage defaultMessage="Enregistrer" description="Button label" />
                </FormButton>
            </div>
            {deleteConfirm ? (
                <DeleteAccountConfirmModal
                    onClosed={onDeleteConfirmClosed}
                    onConfirmed={onDeleteConfirmed}
                />
            ) : null}
        </form>
    );
}

export default ProfileForm;
